// Which way a standing figure faces: east or west, by its last action. A walk faces the way it
// went overall (right and down → right), a blow, a shot or a mend faces whoever it was aimed at, a
// spell the square it was cast at; a shove or a tick (burning, bleeding) turns nobody. The turn
// happens as the action begins (EffectPlayer asks `turnOf` at the start of each clip), and
// `facingAfter` folds a whole pulse so the next board stands everyone the way playback left them —
// the same on every client, since all of them play the same log, skipped or not.
import type { ResolutionEntry, Snapshot, Tile } from '../sim/types';

export type Facing = 1 | -1;

const side = (from: Tile, to: Tile): Facing | undefined => (to.x > from.x ? 1 : to.x < from.x ? -1 : undefined);

/** The turn an entry gives its actor, from where units stand as it begins (`at`). */
export function turnOf(entry: ResolutionEntry, at: (unit: number) => Tile | undefined): { unit: number; face: Facing } | undefined {
  const e = entry.effect;
  if (entry.cause.kind === 'Tick' || entry.cause.kind === 'Event') return undefined;
  switch (e.kind) {
    case 'Moved': {
      const from = e.from ?? at(e.unit), to = e.path[e.path.length - 1];
      const face = from && to ? side(from, to) : undefined;
      return face ? { unit: e.unit, face } : undefined;
    }
    case 'Cast': {
      const face = e.aim ? side(e.from, e.aim) : undefined;
      return face ? { unit: e.unit, face } : undefined;
    }
    case 'Damaged': case 'Healed': {
      if (entry.actor === undefined || entry.actor === e.unit) return undefined;
      const a = at(entry.actor), t = at(e.unit);
      const face = a && t ? side(a, t) : undefined;
      return face ? { unit: entry.actor, face } : undefined;
    }
    default: return undefined;
  }
}

/** Everyone's facing once `entries` have played on `start`, from the facings before (`prior`). */
export function facingAfter(entries: readonly ResolutionEntry[], start: Snapshot, prior: ReadonlyMap<number, Facing>): Map<number, Facing> {
  const pos = new Map(start.units.map((u) => [u.unitId, { ...u.tile }]));
  const out = new Map(prior);
  for (const en of [...entries].sort((a, b) => a.seq - b.seq)) {
    const t = turnOf(en, (u) => pos.get(u));
    if (t) out.set(t.unit, t.face);
    const e = en.effect;
    if (e.kind === 'Moved' && e.path.length) pos.set(e.unit, { ...e.path[e.path.length - 1]! });
    else if (e.kind === 'Displaced') pos.set(e.unit, { ...e.to });
  }
  return out;
}
